import { startOfDay, isSameDay } from 'date-fns';

/**
 * Normalizes a date string or Date object to start of day (midnight) for date-only comparison
 * @param date - ISO date string or Date object
 * @returns Date object set to start of day in local timezone
 */
export const normalizeDateForComparison = (date: string | Date): Date => {
  return startOfDay(new Date(date));
};

/**
 * Checks if a given day conflicts with any dates in the upcoming appointments list
 * Comparison is done date-only (ignoring time components)
 * @param day - The day to check
 * @param upcomingAppointmentDates - Array of ISO date strings representing upcoming appointments
 * @returns true if the day conflicts with any upcoming appointment
 */
export const isDayConflictingWithUpcomingAppointments = (
  day: Date,
  upcomingAppointmentDates: string[],
): boolean => {
  if (!upcomingAppointmentDates || upcomingAppointmentDates.length === 0) {
    return false;
  }

  const normalizedDay = normalizeDateForComparison(day);

  return upcomingAppointmentDates.some(appointmentDateStr => {
    const normalizedAppointmentDate = normalizeDateForComparison(appointmentDateStr);
    return isSameDay(normalizedDay, normalizedAppointmentDate);
  });
};

/**
 * Checks if a given day is the same as the original appointment date (date-only comparison)
 * @param day - The day to check
 * @param originalAppointmentDate - ISO date string of the original appointment
 * @returns true if the day is the same as the original appointment date
 */
export const isDayOriginalAppointment = (day: Date, originalAppointmentDate: string | null): boolean => {
  if (!originalAppointmentDate) {
    return false;
  }

  const normalizedDay = normalizeDateForComparison(day);
  const normalizedOriginalDate = normalizeDateForComparison(originalAppointmentDate);

  return isSameDay(normalizedDay, normalizedOriginalDate);
};

/**
 * Checks if a day should be disabled in the calendar picker
 * A day is disabled if:
 * - It has no available time slots
 * - It's the original appointment date being rescheduled
 * - It conflicts with an upcoming appointment
 *
 * Note: We don't check if the day is outside the reschedule window here because
 * the API query (getRescheduleWindowDates) already filters to only return dates
 * within the valid reschedule window.
 *
 * @param day - The day to check
 * @param timeSlotCount - Number of available time slots for this day
 * @param originalAppointmentDate - ISO date string of the original appointment
 * @param upcomingAppointmentDates - Array of ISO date strings representing upcoming appointments
 * @returns true if the day should be disabled
 */
export const shouldDayBeDisabled = (
  day: Date,
  timeSlotCount: number,
  originalAppointmentDate: string | null,
  upcomingAppointmentDates: string[],
): boolean => {
  if (timeSlotCount === 0) {
    return true;
  }

  if (isDayOriginalAppointment(day, originalAppointmentDate)) {
    return true;
  }

  if (isDayConflictingWithUpcomingAppointments(day, upcomingAppointmentDates)) {
    return true;
  }

  return false;
};

